<template>
    <div>
        <PageHeader :title="'Modelos de Certificado'">
            <template v-slot:icon>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="page-header-icon">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 18.75h-9m9 0a3 3 0 0 1 3 3h-15a3 3 0 0 1 3-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 0 1-.982-3.172M9.497 14.25a7.454 7.454 0 0 0 .981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 0 0 7.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 0 0 2.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 0 1 2.916.52 6.003 6.003 0 0 1-5.395 4.972m0 0a6.726 6.726 0 0 1-2.749 1.35m0 0a6.772 6.772 0 0 1-3.044 0" />
                </svg>
            </template>
        </PageHeader>

        <ActionArea>
            <BackButton :to="{ name: 'eventos' }" />
            <BaseButton skin="primary" @click="openCreate()">
                <template v-slot:icon>
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                </template>
                Adicionar
            </BaseButton>
        </ActionArea>

        <!-- Loading Skeleton -->
        <div v-if="loading" class="table-container">
            <table class="min-w-full">
                <thead>
                    <tr>
                        <th>Nome</th>
                        <th>Assinante</th>
                        <th>Cargo do assinante</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="item in 5" :key="`ct-skeleton-${item}`" class="table-row">
                        <td v-for="cell in 4" :key="`ct-skeleton-cell-${cell}`">
                            <div class="h-3 rounded bg-gray-200 animate-pulse"></div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <Alert text="Ainda não existem modelos de certificado cadastrados" :show="!items.length && !loading" />

        <div class="table-container" v-if="items.length && !loading">
            <table class="min-w-full">
                <thead>
                    <tr>
                        <th>Nome</th>
                        <th>Assinante</th>
                        <th>Cargo do assinante</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr class="table-row" v-for="template in items" :key="template.id">
                        <td>
                            <div class="flex items-center gap-x-3">
                                <BaseButton skin="primary-outline" @click="openPreview(template)">Ver</BaseButton>
                                <span class="text-sm leading-5 text-green-900 font-semibold">{{ template.name }}</span>
                            </div>
                        </td>
                        <td>
                            <span class="text-sm leading-5 text-green-700">{{ template.signatory_name || ' -- ' }}</span>
                        </td>
                        <td>
                            <span class="text-sm leading-5 text-green-700">{{ template.signatory_role || ' -- ' }}</span>
                        </td>
                        <td>
                            <div class="flex gap-x-2">
                                <BaseButton skin="primary-outline" @click="openEdit(template)">Editar</BaseButton>
                                <BaseButton skin="danger-outline" @click="destroy(template.id)">Excluir</BaseButton>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- MODAL -->
        <ModalV2 ref="modalRef" :hide="true" :title="isEditForm ? 'Editar Modelo de Certificado' : 'Novo Modelo de Certificado'" size="lg">
            <template v-slot:body>
                <div class="grid grid-cols-1 gap-y-4">
                    <BaseInput label="Nome" maxlength="120" v-model="form.name" required="true" />
                    <BaseTextarea label="Texto de introdução" rows="4" v-model="form.intro_text" />
                    <BaseTextarea label="Texto de encerramento" rows="4" v-model="form.closing_text" />
                    <div class="grid grid-cols-2 gap-x-8">
                        <BaseInput label="Assinante" maxlength="120" v-model="form.signatory_name" />
                        <BaseInput label="Cargo do assinante" maxlength="120" v-model="form.signatory_role" />
                    </div>

                    <div class="mt-2">
                        <span class="label">Pré-visualização</span>
                        <p class="text-xs text-gray-400 mb-2">Nome, tema e instrutor são exemplos — vêm do evento na hora de gerar.</p>
                        <div class="cert-preview">
                            <img class="cert-logo" src="https://public-assets-megaluzz.s3.us-east-2.amazonaws.com/logo-megaluzz.png" alt="Megaluzz" />
                            <div class="cert-title">CERTIFICADO</div>
                            <div class="cert-body">
                                Certificamos que <strong>Nome do Participante</strong> {{ form.intro_text || '«texto de introdução»' }}
                                <span class="cert-theme">&ldquo;Tema do Treinamento&rdquo;</span>
                                com carga horária total de <strong>8 hora(s)</strong>, ministrado por <strong>Professor(a):</strong> Instrutor, {{ form.closing_text || '«texto de encerramento»' }}
                            </div>
                            <div class="cert-sign">
                                <div class="cert-signature">{{ form.signatory_name || 'Bruna Garcia' }}</div>
                                <div class="cert-sign-line"></div>
                                <div class="cert-sign-name">{{ form.signatory_name || 'Bruna Garcia' }}</div>
                                <div class="cert-sign-role">{{ form.signatory_role || 'CEO • Megaluzz Negócios' }}</div>
                            </div>
                            <div class="cert-footer">Megaluzz Negócios • CNPJ 54.035.435/0001-09 • Cotia/SP</div>
                        </div>
                    </div>
                </div>
            </template>
            <template v-slot:footer>
                <BaseButton skin="primary-outline" @click="closeModal()">Fechar</BaseButton>
                <BaseButton skin="primary" :disabled="loadingSubmit" @click="submit">
                    <svg v-if="loadingSubmit" class="animate-spin ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {{ isEditForm ? 'Atualizar' : 'Salvar' }}
                </BaseButton>
            </template>
        </ModalV2>

        <!-- PREVIEW MODAL -->
        <ModalV2 ref="previewModalRef" :hide="true" :title="previewTemplate.name || 'Pré-visualização do certificado'" size="lg">
            <template v-slot:body>
                <p class="text-xs text-gray-400 mb-2">Nome, tema e instrutor são exemplos — vêm do evento na hora de gerar.</p>
                <div class="cert-preview">
                    <img class="cert-logo" src="https://public-assets-megaluzz.s3.us-east-2.amazonaws.com/logo-megaluzz.png" alt="Megaluzz" />
                    <div class="cert-title">CERTIFICADO</div>
                    <div class="cert-body">
                        Certificamos que <strong>Nome do Participante</strong> {{ previewTemplate.intro_text || '«texto de introdução»' }}
                        <span class="cert-theme">&ldquo;Tema do Treinamento&rdquo;</span>
                        com carga horária total de <strong>8 hora(s)</strong>, ministrado por <strong>Professor(a):</strong> Instrutor, {{ previewTemplate.closing_text || '«texto de encerramento»' }}
                    </div>
                    <div class="cert-sign">
                        <div class="cert-signature">{{ previewTemplate.signatory_name || 'Bruna Garcia' }}</div>
                        <div class="cert-sign-line"></div>
                        <div class="cert-sign-name">{{ previewTemplate.signatory_name || 'Bruna Garcia' }}</div>
                        <div class="cert-sign-role">{{ previewTemplate.signatory_role || 'CEO • Megaluzz Negócios' }}</div>
                    </div>
                    <div class="cert-footer">Megaluzz Negócios • CNPJ 54.035.435/0001-09 • Cotia/SP</div>
                </div>
            </template>
            <template v-slot:footer>
                <BaseButton skin="primary-outline" @click="closePreview()">Fechar</BaseButton>
            </template>
        </ModalV2>
    </div>
</template>

<script>

import { ref } from 'vue';
import Swal from 'sweetalert';
import { CertificateTemplateService } from '../services/Entity';
import BackButton from '@/components/BackButton';
import Alert from '@/components/Alert';
import ModalV2 from '@/components/ModalV2';

export default {
    name: 'CertificateTemplates',
    components: {
        BackButton,
        Alert,
        ModalV2
    },
    setup() {
        const items = ref([]);
        const loading = ref(false);
        const loadingSubmit = ref(false);
        const modalRef = ref();
        const isEditForm = ref(false);
        const previewModalRef = ref();
        const previewTemplate = ref({});
        const form = ref({
            name: '',
            intro_text: '',
            closing_text: '',
            signatory_name: '',
            signatory_role: ''
        });

        function openPreview(template) {
            previewTemplate.value = { ...template };
            previewModalRef.value.setModalStatus('open');
        }

        function closePreview() {
            previewModalRef.value.setModalStatus('close');
        }

        async function init() {
            loading.value = true;
            await loadItems();
            loading.value = false;
        }

        async function loadItems() {
            try {
                const { data: { items: list } } = await CertificateTemplateService.list(1, 9999);
                items.value = list;
            } catch (err) {
                console.log(err);
                Swal({
                    title: 'Ocorreu um erro ao obter os modelos de certificado',
                    text: 'Atualize a página e tente novamente',
                    icon: 'error'
                });
            }
        }

        function resetForm() {
            form.value = {
                name: '',
                intro_text: '',
                closing_text: '',
                signatory_name: '',
                signatory_role: ''
            };
        }

        function openCreate() {
            isEditForm.value = false;
            resetForm();
            modalRef.value.setModalStatus('open');
        }

        function openEdit(template) {
            isEditForm.value = true;
            form.value = {
                id: template.id,
                name: template.name,
                intro_text: template.intro_text || '',
                closing_text: template.closing_text || '',
                signatory_name: template.signatory_name || '',
                signatory_role: template.signatory_role || ''
            };
            modalRef.value.setModalStatus('open');
        }

        function closeModal() {
            modalRef.value.setModalStatus('close');
        }

        function buildPayload() {
            return {
                name: form.value.name,
                intro_text: form.value.intro_text,
                closing_text: form.value.closing_text,
                signatory_name: form.value.signatory_name,
                signatory_role: form.value.signatory_role
            };
        }

        async function submit() {
            if (!form.value.name?.trim()) {
                Swal({ title: 'Informe o nome do modelo de certificado', icon: 'info' });
                return;
            }

            try {
                loadingSubmit.value = true;

                if (isEditForm.value) {
                    await CertificateTemplateService.update(form.value.id, buildPayload());
                    Swal({ title: 'Atualizado!', icon: 'success' });
                } else {
                    await CertificateTemplateService.create(buildPayload());
                    Swal({ title: 'Salvo!', icon: 'success' });
                }

                closeModal();
                await loadItems();
            } catch (err) {
                console.log(err);
                Swal({
                    title: 'Ocorreu um erro ao salvar',
                    text: 'Atualize a página e tente novamente',
                    icon: 'error'
                });
            } finally {
                loadingSubmit.value = false;
            }
        }

        async function destroy(id) {
            const confirm = await Swal({
                title: 'Tem certeza que deseja excluir esse modelo de certificado?',
                text: 'Uma vez que excluir, não será possível recuperar',
                icon: 'warning',
                buttons: {
                    cancel: 'Cancelar',
                    confirm: {
                        text: 'Excluir',
                        className: 'swal-button--danger'
                    }
                },
                dangerMode: true
            });

            if (!confirm) return;

            try {
                loading.value = true;
                await CertificateTemplateService.destroy(id);
                await loadItems();
                Swal({ title: 'Excluído', icon: 'success' });
            } catch (err) {
                console.log(err);
                Swal({
                    title: 'Ocorreu um erro ao excluir',
                    text: 'Atualize a página e tente novamente',
                    icon: 'error'
                });
            } finally {
                loading.value = false;
            }
        }

        init();

        return {
            items,
            loading,
            loadingSubmit,
            modalRef,
            isEditForm,
            previewModalRef,
            previewTemplate,
            form,
            openCreate,
            openEdit,
            openPreview,
            closePreview,
            closeModal,
            submit,
            destroy
        };
    }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap');

.cert-preview {
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #fff;
    padding: 24px;
    text-align: center;
    color: #3a3a3a;
}
.cert-preview .cert-logo { width: 120px; height: auto; margin: 0 auto 12px; display: block; }
.cert-preview .cert-title { font-size: 22px; font-weight: 700; letter-spacing: 1px; color: #1e2a59; margin: 4px 0 16px; }
.cert-preview .cert-body { font-size: 13px; line-height: 1.8; max-width: 560px; margin: 0 auto; }
.cert-preview .cert-body strong { color: #1e2a59; }
.cert-preview .cert-theme { display: block; font-size: 14px; font-style: italic; font-weight: 600; color: #1e2a59; margin: 10px auto; }
.cert-preview .cert-sign { margin-top: 28px; }
.cert-preview .cert-signature { font-family: 'Great Vibes', cursive; font-size: 26px; color: #1e2a59; line-height: 1; }
.cert-preview .cert-sign-line { width: 180px; margin: 2px auto 4px; border-bottom: 1px solid #9aa0a6; }
.cert-preview .cert-sign-name { font-size: 12px; font-weight: 700; color: #2a2a2a; }
.cert-preview .cert-sign-role { font-size: 10px; color: #5c5c5c; }
.cert-preview .cert-footer { margin-top: 20px; font-size: 9px; color: #8a8a8a; }
</style>
